import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from '../ui/card';
import { cn } from '../../lib/utils';

export const MetricCard = (props) => {
  const {
    title,
    value,
    subtitle,
    trend,
    trendValue,
    icon: Icon,
    status,
    className,
    onClick,
  } = props;
  const dataTestId = props['data-testid'];
  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend === 'up') return <TrendingUp className="w-4 h-4" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const getTrendColor = () => {
    if (!trend) return '';
    if (trend === 'up') return 'text-[rgb(var(--success))]';
    if (trend === 'down') return 'text-[rgb(var(--destructive))]';
    return 'text-muted-foreground';
  };

  const getStatusClass = () => {
    if (!status) return '';
    if (status === 'up' || status === 'healthy' || status === 'on_track') return 'border-l-4 border-l-[rgb(var(--success))]';
    if (status === 'degraded' || status === 'at_risk') return 'border-l-4 border-l-[rgb(var(--warning))]';
    if (status === 'down' || status === 'critical' || status === 'off_track') return 'border-l-4 border-l-[rgb(var(--destructive))]';
    return '';
  };

  return (
    <Card
      data-testid={dataTestId}
      className={cn(
        'metric-card p-6 flex flex-col justify-between h-full bg-card/80 backdrop-blur-sm border-border/50 hover:border-border cursor-pointer',
        getStatusClass(),
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        {Icon && (
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon className="w-4 h-4 text-primary" />
          </div>
        )}
      </div>
      
      <div className="space-y-1">
        <div className="metric-value text-foreground">
          {value}
        </div>
        
        <div className="flex items-center gap-2">
          {subtitle && (
            <span className="text-sm text-muted-foreground">{subtitle}</span>
          )}
          {trendValue && (
            <span className={cn('flex items-center gap-1 text-sm font-medium', getTrendColor())}>
              {getTrendIcon()}
              {trendValue}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};

export const StatusCard = (props) => {
  const {
    title,
    status,
    message,
    lastUpdate,
    icon: Icon,
    className,
  } = props;
  const dataTestId = props['data-testid'];
  const getStatusColor = () => {
    if (status === 'up' || status === 'healthy') return 'bg-[rgb(var(--success))]';
    if (status === 'degraded') return 'bg-[rgb(var(--warning))]';
    return 'bg-[rgb(var(--destructive))]';
  };

  const getStatusText = () => {
    if (status === 'up' || status === 'healthy') return 'Operational';
    if (status === 'degraded') return 'Degraded';
    return 'Down';
  };

  return (
    <Card
      data-testid={dataTestId}
      className={cn(
        'metric-card p-6 bg-card/80 backdrop-blur-sm border-border/50',
        className
      )}
    >
      <div className="flex items-start justify-between mb-4">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        {Icon && (
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon className="w-4 h-4 text-primary" />
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('w-3 h-3 rounded-full animate-pulse', getStatusColor())} />
        <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {getStatusText()}
        </span>
      </div>
      
      {message && (
        <p className="text-sm text-muted-foreground mb-2">{message}</p>
      )}
      
      {lastUpdate && (
        <p className="text-xs text-muted-foreground">
          Last updated: {lastUpdate}
        </p>
      )}
    </Card>
  );
};
